import { Box, Button, Divider, Theme, Tooltip, Typography, useMediaQuery } from '@evaneos/b2b-ui';
import cx from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import Dropzone, { type FileRejection } from 'react-dropzone';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import AccessTimeOutlinedIcon from '@evaneos/b2b-ui/icons/AccessTimeOutlined';
import AttachFileIcon from '@evaneos/b2b-ui/icons/AttachFile';
import InsertDriveFileOutlinedIcon from '@evaneos/b2b-ui/icons/InsertDriveFileOutlined';
import SendIcon from '@evaneos/b2b-ui/icons/Send';
import TextFormatIcon from '@evaneos/b2b-ui/icons/TextFormat';

import { useIsMutating } from '@tanstack/react-query';

import { datadogLogger } from '../../../../../shared/datadog/logger';
import { ERROR, PENDING, SUCCESS } from '../../../../../design-system/constants';
import { AttachFile } from '../../../../../design-system/Icons/AttachFile';
import { Done } from '../../../../../design-system/Icons/Done';
import { CircularLoader } from '../../../../../design-system/Loader/CircularLoader';
import {
    generateSendingMessageId,
    saveDraftMessage,
    sendMessageRequest,
} from '../../../../../resources/discussion';
import { generateAttachmentId } from '../../../../../resources/discussion/operations';
import {
    getDraftMessage,
    getSendingMessageStatus,
    saveDraftMessageStatus,
} from '../../../../../resources/discussion/selectors';
import { uploadAttachment } from '../../../../../resources/discussion/api';
import {
    Attachment as AttachmentType,
    AttachmentWithStatus,
    AttachmentsById,
    DraftMessageMetadata,
} from '../../../../../resources/discussion/types';
import { DossierId } from '../../../../../resources/dossier/types';
import { useAuth } from '../../../../../shared/providers/AuthProvider';
import { MessageTemplate } from '../../../../../resources/message-template/types';
import { debounce } from '../../../../../resources/pipeline/utils';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
    SendMessageClickedProperties,
} from '../../../../../shared/analytics';
import { useAppDispatch, useAppSelector } from '../../../../../shared/redux';
import { levenshteinSimilarity } from '../../../../../shared/utilities/levenshteinSimilarity';
import {
    EditorContent,
    QuillEditor,
} from '../../../../../shared/components/QuillEditor/QuillEditor';
import { useHeightObserver } from '../../../../../shared/hooks/useHeightObserver';
import { useFilteredDiscussionGroupedByDate } from '../../../../../resources/discussion/queries';
import { convertSpacesInHtml } from '../../utils/messageHtmlConverter';

import { generateSummaryMailMutationKey } from '../../../../../resources/phoning/mutations';

import { AddAttachmentsModal } from './AddAttachmentsModal';
import { Attachment } from './Attachment';
import { ScheduleMessageDialog } from './ScheduleMessageDialog';
import { AT1ProposalResponseData } from './fetchAT1Proposal';

import s from './NewMessageBox.module.css';

export const MAX_FILE_SIZE_MB = 32;

export const tooltipSlotProps = {
    popper: {
        modifiers: [{ name: 'offset', options: { offset: [0, -10] } }], // Effectively 4px distance with source element
    },
};

function getSubmitMessageTrackingData(
    currentMessage: string,
    at1ProposalData: AT1ProposalResponseData | null,
    draftMessageMetadata?: DraftMessageMetadata,
): {
    similarityScore: number;
    messageSource: SendMessageClickedProperties['message_source'];
} {
    if (at1ProposalData) {
        return {
            similarityScore: levenshteinSimilarity(currentMessage, at1ProposalData.content || ''),
            messageSource: 'at1_suggestion',
        };
    }

    if (
        draftMessageMetadata?.origin === 'phone_call_summary' &&
        draftMessageMetadata.originalContent
    ) {
        return {
            similarityScore: levenshteinSimilarity(
                currentMessage,
                draftMessageMetadata.originalContent,
            ),
            messageSource: 'call_recap',
        };
    }

    return {
        similarityScore: -1,
        messageSource: 'manual',
    };
}

type Props = {
    handleMessageTemplatesPanel: () => void;
    onHeightChange: () => void;
    selectedMessageTemplate: MessageTemplate | null;
    removeSelectedMessageTemplate: () => void;
    disableAutoFocus?: boolean;
    getAttachments: (sendingMessageId: string) => AttachmentsById;
    addAttachment: (
        sendingMessageId: string,
        filenameId: string,
        attachment: AttachmentWithStatus,
    ) => void;
    removeAttachment: (sendingMessageId: string, filenameId: string) => void;
    proposalStatus: 'idle' | 'loading' | 'success' | 'error';
    at1ProposalData: AT1ProposalResponseData | null;
    onAT1ProposalConsumed: () => void;
};

/* eslint-disable-next-line max-lines-per-function -- message composition with multiple status-based conditionals */
export const NewMessageBox = (props: Props): JSX.Element => {
    const {
        selectedMessageTemplate,
        removeSelectedMessageTemplate,
        onHeightChange,
        handleMessageTemplatesPanel,
        disableAutoFocus = false,
        getAttachments,
        addAttachment,
        removeAttachment,
        proposalStatus,
        at1ProposalData,
        onAT1ProposalConsumed,
    } = props;

    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const { isImpersonated } = useAuth();
    const { draftMessage, htmlDraftMessage, draftMessageMetadata } = useAppSelector(
        getDraftMessage(dossierId),
    );
    const saveDraftStatus = useAppSelector(saveDraftMessageStatus(dossierId));
    const sendingMessageStatus = useAppSelector(getSendingMessageStatus(dossierId));
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { scheduledMessage: discussionScheduledMessage } =
        useFilteredDiscussionGroupedByDate(dossierId);

    const isGeneratingSummaryMailForDossier =
        useIsMutating({ mutationKey: generateSummaryMailMutationKey(dossierId) }) > 0;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const editorContainerRef = useHeightObserver<HTMLElement>(onHeightChange);
    const draftMessageMetadataRef = useRef(draftMessageMetadata);

    const [content, setContent] = useState<EditorContent | null>({
        text: draftMessage,
        html: htmlDraftMessage,
    });
    const [files, setFiles] = useState<Array<File>>([]);
    const [sendingMessageId, setSendingMessageId] = useState<string>(generateSendingMessageId());
    const attachments = getAttachments(sendingMessageId);
    const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState<boolean>(false);
    const [showToolbar, setShowToolbar] = useState<boolean>(true);
    const [attachmentError, setAttachmentError] = useState<'file_size' | 'upload' | null>(null);

    const [prevTemplate, setPrevTemplate] = useState(selectedMessageTemplate);
    if (prevTemplate !== selectedMessageTemplate) {
        setPrevTemplate(selectedMessageTemplate);
        if (selectedMessageTemplate !== null) {
            setContent({
                text: selectedMessageTemplate.content,
                html: convertSpacesInHtml(selectedMessageTemplate.content),
            });
        }
    }

    const [prevAt1ProposalData, setPrevAt1ProposalData] = useState(at1ProposalData);
    if (prevAt1ProposalData !== at1ProposalData && at1ProposalData?.content) {
        setPrevAt1ProposalData(at1ProposalData);
        setContent({
            text: at1ProposalData.content,
            html: convertSpacesInHtml(at1ProposalData.content),
        });
    }

    useEffect(() => {
        draftMessageMetadataRef.current = draftMessageMetadata;
    }, [draftMessageMetadata]);

    const attachmentsList = Object.values(attachments);
    const attachmentIds = attachmentsList.map((att) => att.id).filter((id) => id !== undefined);
    const hasNoMessage = content === null || content.text.trim() === '';
    const messageIsNotSending =
        !sendingMessageStatus ||
        !sendingMessageStatus[sendingMessageId] ||
        (!!sendingMessageStatus && sendingMessageStatus[sendingMessageId]?.postContent !== PENDING);

    const isSchedulingDisabled = hasNoMessage || !!discussionScheduledMessage;

    const debouncedSaveDraft = useMemo(
        () =>
            debounce(
                1000,
                (
                    id: DossierId,
                    draftMessage: string,
                    htmlDraftMessage: string,
                    metadata?: typeof draftMessageMetadata,
                ) => {
                    if (messageIsNotSending) {
                        dispatch(saveDraftMessage(id, draftMessage, htmlDraftMessage, metadata));
                    }
                },
            ),
        [dispatch, messageIsNotSending],
    );

    // Side effect: deselect the template after it has been applied
    useEffect(() => {
        if (selectedMessageTemplate !== null) {
            removeSelectedMessageTemplate();
        }
    }, [selectedMessageTemplate, removeSelectedMessageTemplate]);

    // override the message box content if there's a draft from phone call summary
    const [prevDraftSnapshot, setPrevDraftSnapshot] = useState({
        message: draftMessage,
        html: htmlDraftMessage,
        origin: draftMessageMetadata?.origin,
    });
    const currentOrigin = draftMessageMetadata?.origin;
    if (
        prevDraftSnapshot.message !== draftMessage ||
        prevDraftSnapshot.html !== htmlDraftMessage ||
        prevDraftSnapshot.origin !== currentOrigin
    ) {
        setPrevDraftSnapshot({
            message: draftMessage,
            html: htmlDraftMessage,
            origin: currentOrigin,
        });
        if (currentOrigin === 'phone_call_summary') {
            setContent({ text: draftMessage, html: htmlDraftMessage });
        }
    }

    useEffect(() => {
        const textAreaScrollDown = () => {
            if (textareaRef.current) {
                textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
            }
        };
        textAreaScrollDown();
    }, []);

    function handleChangeEnrichText(content: EditorContent): void {
        setContent(content);

        if (isImpersonated) {
            return;
        }

        debouncedSaveDraft(dossierId, content.text, content.html, draftMessageMetadataRef.current);
    }

    function handleSubmit(): void {
        const currentMessage = content?.text ?? '';
        const { similarityScore, messageSource } = getSubmitMessageTrackingData(
            currentMessage,
            at1ProposalData,
            draftMessageMetadataRef.current,
        );

        trackEvent((ampli, defaultProperties) => {
            ampli.sendMessageClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                similarity_score: similarityScore,
                is_at1: !!at1ProposalData,
                message_source: messageSource,
            });
        });

        if (content) {
            dispatch(
                sendMessageRequest({
                    dossierId,
                    sendingMessageId,
                    content: content.text,
                    contentHtml: content.html,
                    attachmentIds,
                }),
            );
            setContent(null);
        }
        setSendingMessageId(generateSendingMessageId());

        if (at1ProposalData) {
            onAT1ProposalConsumed();
        }
    }

    // Move into own attachment container
    function selectAttachments(files: File[]): void {
        setAttachmentError(null);
        trackEvent((ampli, defaultProperties) => {
            ampli.discussionAddADocumentClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        setFiles([...files]);
    }

    function handleDropRejected(fileRejections: FileRejection[]): void {
        const hasSizeError = fileRejections.some((f) =>
            f.errors.some((e) => e.code === 'file-too-large'),
        );

        if (hasSizeError) {
            setAttachmentError('file_size');
        } else {
            datadogLogger.error('[Discussion]: Attachment drop rejected', {
                rejections: fileRejections.map((f) => ({
                    fileName: f.file.name,
                    fileSize: f.file.size,
                    errors: f.errors.map((e) => ({ code: e.code, message: e.message })),
                })),
            });
            setAttachmentError('upload');
        }
    }

    function uploadAttachments(attachmentsToUpload: Array<AttachmentType>): void {
        setAttachmentError(null);
        attachmentsToUpload.forEach((attachment) => {
            const filenameId = generateAttachmentId(attachment.file.name);
            const baseAttachment = {
                file: attachment.file,
                category: attachment.category,
                filenameId,
            };

            addAttachment(sendingMessageId, filenameId, { ...baseAttachment, status: 'pending' });

            uploadAttachment(attachment.file, attachment.category)
                .then((data) => {
                    addAttachment(sendingMessageId, filenameId, {
                        ...baseAttachment,
                        id: data.id.toString(),
                        status: 'success',
                    });
                })
                .catch((error) => {
                    datadogLogger.error('[Discussion]: Attachment upload failed', {
                        fileName: attachment.file.name,
                        fileSize: attachment.file.size,
                        category: attachment.category,
                        errorMessage: error instanceof Error ? error.message : String(error),
                    });
                    setAttachmentError('upload');
                    addAttachment(sendingMessageId, filenameId, {
                        ...baseAttachment,
                        status: 'error',
                    });
                });
        });
    }

    function handleClickAddTemplate(): void {
        handleMessageTemplatesPanel();
        trackEvent((ampli, defaultProperties) => {
            ampli.discussionAddATemplateClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    }

    function handleOpenScheduleDialog() {
        trackEvent((ampli, defaultProperties) => {
            ampli.scheduledMessageOpenModal({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });

        setIsScheduleDialogOpen(true);
    }
    function handleScheduleMessageSuccess(): void {
        setIsScheduleDialogOpen(false);
        setContent(null);
        // Reset sending message ID after successful scheduling
        setSendingMessageId(generateSendingMessageId());
    }

    return (
        <>
            <Dropzone
                onDrop={selectAttachments}
                onDropRejected={handleDropRejected}
                maxSize={MAX_FILE_SIZE_MB * 1_000_000}
                noClick
            >
                {
                    // eslint-disable-next-line max-lines-per-function -- render prop callback contains full dropzone UI layout
                    ({ getRootProps, getInputProps, isDragActive, open }) => (
                        <Box
                            sx={{
                                padding: {
                                    xs: '4px 16px 0 16px',
                                    lg: '4px 24px 0 24px',
                                },
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                            }}
                            {...getRootProps()}
                        >
                            <AddAttachmentsModal
                                key={files.length}
                                inputs={files}
                                isOpen={files.length > 0}
                                dossierId={dossierId}
                                onClose={() => setFiles([])}
                                onSend={(attachments) => {
                                    uploadAttachments(attachments);
                                    setFiles([]);
                                }}
                            />
                            <Box
                                ref={editorContainerRef}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid',
                                    borderColor: 'border.main',
                                    borderRadius: '4px',
                                    width: '100%',
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <QuillEditor
                                    value={content?.html ?? ''}
                                    onTextChange={handleChangeEnrichText}
                                    autoFocus={!disableAutoFocus}
                                    className={cx('quill-editor-container', s.enrichTextarea)}
                                    readOnly={proposalStatus === 'loading'}
                                    placeholder={formatMessage({
                                        id: 'discussion.input_placeholder',
                                    })}
                                    showToolbar={showToolbar}
                                />

                                {attachmentsList.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {attachmentsList.map((attachment) => (
                                            <Attachment
                                                attachment={attachment}
                                                key={attachment.filenameId}
                                                onDelete={() => {
                                                    removeAttachment(
                                                        sendingMessageId,
                                                        attachment.filenameId,
                                                    );
                                                }}
                                                retryUploadAttachment={(attachment) =>
                                                    uploadAttachments([
                                                        {
                                                            file: attachment.file,
                                                            category: attachment.category,
                                                        },
                                                    ])
                                                }
                                            />
                                        ))}
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        display: 'flex',
                                        padding: 1.5,
                                        gap: 1,
                                        '& svg': { fontSize: '2.4rem' },
                                    }}
                                >
                                    <Tooltip
                                        title={formatMessage({ id: 'discussion.add_attachments' })}
                                        placement="top"
                                        disableInteractive
                                        slotProps={tooltipSlotProps}
                                    >
                                        <Button
                                            onClick={open}
                                            variant="text"
                                            color="secondary"
                                            sx={{ width: '40px', minWidth: '40px' }}
                                        >
                                            <input
                                                {...getInputProps()}
                                                data-testid="upload-attachment-input"
                                                data-spec-id="upload-attachment-input"
                                                style={{ display: 'none' }}
                                            />
                                            <AttachFileIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip
                                        title={formatMessage({ id: 'discussion.add_template' })}
                                        placement="top"
                                        disableInteractive
                                        slotProps={tooltipSlotProps}
                                    >
                                        <Button
                                            onClick={handleClickAddTemplate}
                                            variant="text"
                                            color="secondary"
                                            sx={{ width: '40px', minWidth: '40px' }}
                                        >
                                            <InsertDriveFileOutlinedIcon />
                                        </Button>
                                    </Tooltip>

                                    <Divider orientation="vertical" flexItem />
                                    <Tooltip
                                        title={formatMessage({
                                            id: showToolbar
                                                ? 'discussion.editor.hide_format_toolbar'
                                                : 'discussion.editor.show_format_toolbar',
                                        })}
                                        placement="top"
                                        disableInteractive
                                        slotProps={tooltipSlotProps}
                                    >
                                        <Button
                                            onClick={() => setShowToolbar(!showToolbar)}
                                            variant="text"
                                            color="secondary"
                                            sx={{ width: '40px', minWidth: '40px' }}
                                            aria-label={formatMessage({
                                                id: showToolbar
                                                    ? 'discussion.editor.hide_format_toolbar'
                                                    : 'discussion.editor.show_format_toolbar',
                                            })}
                                        >
                                            <TextFormatIcon />
                                        </Button>
                                    </Tooltip>

                                    <Box flex={1} />

                                    <Tooltip
                                        title={formatMessage({
                                            id: 'discussion.schedule_messages.modal.submit',
                                        })}
                                        placement="top"
                                        disableInteractive
                                        slotProps={tooltipSlotProps}
                                    >
                                        <span>
                                            <Button
                                                disabled={isSchedulingDisabled}
                                                aria-label={formatMessage({
                                                    id: 'discussion.schedule_messages.modal.submit',
                                                })}
                                                // eslint-disable-next-line sonarjs/void-use -- explicit void for fire-and-forget async
                                                onClick={() => void handleOpenScheduleDialog()}
                                                variant={
                                                    isSchedulingDisabled ? 'contained' : 'outlined'
                                                }
                                                color="secondary"
                                                size="medium"
                                                sx={{
                                                    width: '40px',
                                                    height: '40px',
                                                    minWidth: '40px',
                                                    transitionProperty: 'background-color, color',
                                                }}
                                            >
                                                <AccessTimeOutlinedIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Button
                                        onClick={() => handleSubmit()}
                                        disabled={
                                            hasNoMessage ||
                                            proposalStatus === 'loading' ||
                                            isGeneratingSummaryMailForDossier
                                        }
                                        data-testid="send-button"
                                        data-spec-id="send-button"
                                        sx={{
                                            height: '40px',
                                            ...(isMobile && { width: '40px', minWidth: '40px' }),
                                        }}
                                    >
                                        {isMobile ? (
                                            <SendIcon />
                                        ) : (
                                            formatMessage({ id: 'global.send' })
                                        )}
                                    </Button>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    height: '20px',
                                    marginY: '4px',
                                }}
                            >
                                {attachmentError && (
                                    <Typography
                                        variant="caption"
                                        data-testid="attachment-error"
                                        sx={{ color: 'error.main', marginRight: 'auto' }}
                                    >
                                        {attachmentError === 'file_size'
                                            ? formatMessage(
                                                  { id: 'discussion.attachment.file_size_error' },
                                                  { maxSize: MAX_FILE_SIZE_MB },
                                              )
                                            : formatMessage({
                                                  id: 'discussion.attachment.upload_error',
                                              })}
                                    </Typography>
                                )}
                                {saveDraftStatus === PENDING && (
                                    <div className={s.saving}>
                                        <CircularLoader
                                            className={s.draftLoader}
                                            color={'var(--ink)'}
                                            thickness={3}
                                        />
                                        {formatMessage({ id: 'global.saving' })}
                                        {'...'}
                                    </div>
                                )}
                                {saveDraftStatus === SUCCESS && (
                                    <span className={s.saved} data-testid="draft-saved">
                                        <Done color="var(--ink)" />
                                        {formatMessage({ id: 'global.saved' })}
                                    </span>
                                )}
                                {saveDraftStatus === ERROR && (
                                    <p className={s.error}>
                                        {formatMessage({
                                            id: 'discussion.draft_message.saving_error',
                                        }) + ' '}
                                        <a
                                            onClick={() => {
                                                dispatch(
                                                    saveDraftMessage(
                                                        dossierId,
                                                        content?.text ?? '',
                                                        content?.html ?? '',
                                                        draftMessageMetadataRef.current,
                                                    ),
                                                );
                                            }}
                                            className={s.retry}
                                            data-testid="draft-retry"
                                        >
                                            {formatMessage({
                                                id: 'global.retry',
                                            })}
                                        </a>
                                    </p>
                                )}
                            </Box>

                            {isDragActive && (
                                <div className={s.dragOverlay}>
                                    <AttachFile
                                        color="var(--ink-light)"
                                        width="20px"
                                        height="20px"
                                    />
                                    {formatMessage({ id: 'discussion.drop_file' })}
                                </div>
                            )}
                        </Box>
                    )
                }
            </Dropzone>

            <ScheduleMessageDialog
                open={isScheduleDialogOpen}
                onClose={() => setIsScheduleDialogOpen(false)}
                onSuccess={handleScheduleMessageSuccess}
                editorContent={content}
                sendingMessageId={sendingMessageId}
                attachmentIds={attachmentIds}
            />
        </>
    );
};
